import { useReducedMotion } from 'motion/react';
import { cn } from '@/src/infrastructure/lib/utils';
import React from 'react';

type ParticleEffect =
  | 'petals' | 'yellow_dust' | 'sparkles' | 'embers' | 'none'
  | 'confetti' | 'balloons' | 'fireflies' | 'snowfall' | 'bubbles' | 'rose_petals';

interface ParticlesProps {
  effect: ParticleEffect;
  reduce?: boolean;
}

export function Particles({ effect, reduce = false }: ParticlesProps) {
  const prefersReducedMotion = useReducedMotion();
  if (prefersReducedMotion || effect === 'none') return null;

  // Reduce count for mobile/performance
  const particleCount = reduce ? 8 : 20;
  const particles = Array.from({ length: particleCount }, (_, i) => i);

  // Deterministic randomness seeded by index
  const seededRandom = (seed: number) => {
    const x = Math.sin(seed * 37) * 10000;
    return x - Math.floor(x);
  };

  const getAnimationConfig = (index: number) => {
    const delay = seededRandom(index * 13) * 4;
    const duration = 8 + seededRandom(index * 17) * 6;
    const x = seededRandom(index * 23) * 100;
    return { delay, duration, x };
  };

  const effectStyles: Record<ParticleEffect, string> = {
    petals: 'w-2 h-2 rounded-full bg-pink-200 opacity-60',
    yellow_dust: 'w-1.5 h-1.5 rounded-full bg-yellow-300 opacity-50',
    sparkles: 'w-1 h-1 rounded-full bg-white opacity-80',
    embers: 'w-1.5 h-1.5 rounded-full bg-orange-400 opacity-60',
    none: '',
    confetti: 'w-2 h-4 bg-yellow-400 opacity-70', // rectangle
    balloons: 'w-6 h-8 rounded-full bg-red-400 opacity-60', // balloon shape
    fireflies: 'w-1.5 h-1.5 rounded-full bg-yellow-200 opacity-80 shadow-[0_0_8px_4px_rgba(254,240,138,0.6)]',
    snowfall: 'w-2 h-2 rounded-full bg-white opacity-60',
    bubbles: 'w-4 h-4 rounded-full border-[1.5px] border-blue-200 opacity-40 bg-blue-50/10',
    rose_petals: 'w-3 h-3 rounded-full bg-rose-500 opacity-70',
  };

  // Determine direction and keyframe name based on effect
  const isUpward = ['bubbles', 'balloons'].includes(effect);
  const isJittery = ['fireflies'].includes(effect);
  const isSpinning = ['confetti', 'rose_petals'].includes(effect);
  
  let animationName = 'particle-fall';
  if (isUpward) animationName = 'particle-float-up';
  if (isJittery) animationName = 'particle-jitter';
  if (isSpinning) animationName = 'particle-spin-fall';

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((i) => {
        const { delay, duration, x } = getAnimationConfig(i);
        
        // Add random rotation and colors for confetti
        const randomColor = effect === 'confetti' ? ['bg-yellow-400', 'bg-blue-400', 'bg-red-400', 'bg-green-400'][Math.floor(seededRandom(i * 11) * 4)] : '';
        const baseStyle = effectStyles[effect];
        
        // Balloons can be multiple colors too
        const balloonColor = effect === 'balloons' ? ['bg-red-400', 'bg-blue-400', 'bg-green-400'][Math.floor(seededRandom(i * 11) * 3)] : '';

        return (
          <div
            key={i}
            className={cn(`absolute ${baseStyle}`, effect === 'confetti' && randomColor, effect === 'balloons' && balloonColor)}
            style={{
              left: `${x}%`,
              // Start below for upward, above for downward
              top: isUpward ? '110%' : '-10%',
              animation: `${animationName} ${duration}s linear ${delay}s infinite`,
              '--x-offset': `${(x - 50) * 0.3}px`,
              '--rotation-amount': `${seededRandom(i * 5) * 360}deg`,
            } as React.CSSProperties}
          />
        );
      })}

      <style>{`
        @keyframes particle-fall {
          0% {
            opacity: 1;
            transform: translateY(0) translateX(0);
          }
          100% {
            opacity: 0;
            transform: translateY(110vh) translateX(var(--x-offset, 0px));
          }
        }
        @keyframes particle-float-up {
          0% {
            opacity: 1;
            transform: translateY(0) translateX(0);
          }
          100% {
            opacity: 0;
            transform: translateY(-110vh) translateX(calc(var(--x-offset, 0px) * -1));
          }
        }
        @keyframes particle-jitter {
          0%, 100% { opacity: 0; transform: translateY(0) translateX(0) scale(0.8); }
          50% { opacity: 1; transform: translateY(50vh) translateX(var(--x-offset, 0px)) scale(1.2); }
        }
        @keyframes particle-spin-fall {
          0% {
            opacity: 1;
            transform: translateY(0) translateX(0) rotate(0deg);
          }
          100% {
            opacity: 0;
            transform: translateY(110vh) translateX(var(--x-offset, 0px)) rotate(var(--rotation-amount, 360deg));
          }
        }
      `}</style>
    </div>
  );
}
